import { useCallback, useReducer, useState } from 'react';
import { useMount } from 'react-use';

import { SortBy, defaultSortBy } from '../../constants/sortData';
import { getAllFiltersFromPrams } from '../../elements/FilterBar/utils';
import useActions from '../../hooks/useActions';
import { getSearchWithTokenActions } from '../../redux/actions/getSearchWithTokenActions';
import { getVendorDetailActions } from '../../redux/actions/getVendorDetailActions';
import { getListItemPerPage } from '../../redux/api/utils';
import { makeDataByIdKey } from '../../redux/selectors/vendorDetailSelector';
import { useSelector } from '../../redux/utils';
import { ItemTypes, ListLoadingTypes } from '../../types/Common';
import { SearchUserSubmitItem } from '../../types/SearchUserSubmit';
import { Data as VendorDetailData } from '../../types/VendorDetailResponse';
import ItemSources from '../../utils/ItemSources';
import * as navigationService from '../../utils/navigationService';
import * as routeUtils from '../../utils/routes/utils';
import { useRoute } from '../../utils/useRoute';
import listReducer, { defaultState } from './listReducer';

function replaceNotFound(from: string) {
  const shouldWebReplace = true;

  navigationService.replace(
    'NotFoundPage',
    {
      from,
    },
    shouldWebReplace
  );
}

export const useData = (vendorId: string) => {
  const { params } = useRoute();

  const [sources] = useState(() => {
    return new ItemSources<SearchUserSubmitItem>({
      productKey: product => `${product.id}`,
      productSource: () => ItemTypes.USER_SUBMIT,
    });
  });
  const [listData, dispatch] = useReducer(listReducer, defaultState);

  const [getVendorDetail, getSearchWithToken] = useActions([
    getVendorDetailActions.request,
    getSearchWithTokenActions.request,
  ]);

  const sortBy: SortBy = params?.sortBy || defaultSortBy;

  const fetchData = useCallback(
    (args: { type: ListLoadingTypes; page: number; topIds?: string }) => {
      const { type, page, topIds } = args;
      const perPage = getListItemPerPage();
      const filterData = getAllFiltersFromPrams(params || {});

      getSearchWithToken({
        ...filterData,
        dispatch,
        loadingType: type,
        page,
        per_page: perPage,
        seller_id: vendorId,
        success: sources.processSource,
        sortBy,
        topIds,
      });
    },
    [getSearchWithToken, sources, vendorId, params, sortBy]
  );

  const fetchVendorDetail = useCallback(
    (id: string) => {
      getVendorDetail({
        failure: () => {
          replaceNotFound(
            encodeURIComponent(
              routeUtils.compileToPath('VendorPage', { id: vendorId })
            )
          );
        },
        success: (data: VendorDetailData) => {
          if (data.seller.is_offline) {
            replaceNotFound(
              encodeURIComponent(
                routeUtils.compileToPath('VendorPage', { id: vendorId })
              )
            );

            return;
          }

          fetchData({ type: 'new', page: 1, topIds: data.seller.top_ids });
        },
        id,
      });
    },
    [getVendorDetail, fetchData]
  );

  const { products, isFetching, currentPage, hasMoreItems } = listData;

  useMount(() => {
    fetchVendorDetail(vendorId);
  });

  const vendorName = useSelector(makeDataByIdKey(vendorId, 'customize_name'));
  const description = useSelector(makeDataByIdKey(vendorId, 'desc_text'));
  const headerBanner = useSelector(
    makeDataByIdKey(vendorId, 'header_banner_urls')
  );
  const eventBanner = useSelector(
    makeDataByIdKey(vendorId, 'brand_event_banner_urls')
  );

  return {
    sortBy,
    description,
    fetchData,
    sources,
    products,
    isFetching,
    currentPage,
    hasMoreItems,
    vendorName,
    headerBanner,
    eventBanner,
  };
};
